import { CheckCircle2, FlaskConical, XCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { LoadGrade } from "@/features/intake/api/types"
import { QualityMathDialog } from "@/features/intake/components/quality-math"
import { gradeTone } from "@/features/intake/lib/intake-status"
import {
  classifyGradeWithDriver,
  computeRefractionKg,
  type ClassificationInputs,
  type RefractionInputs,
} from "@/features/intake/lib/quality-rules"
import { formatNumber } from "@/lib/format"

interface LiveGradePanelProps {
  refraction: RefractionInputs
  classification: ClassificationInputs
  acceptedKg: number
  ready: boolean
  stamped?: boolean
  stampedGrade?: LoadGrade | null
  hideRefractionTotal?: boolean
}

function formatLocaleNumber(n: number, decimals = 2): string {
  return formatNumber(n, decimals)
}

export function LiveGradePanel({
  refraction,
  classification,
  acceptedKg,
  ready,
  stamped = false,
  stampedGrade,
  hideRefractionTotal = false,
}: LiveGradePanelProps) {
  const { t } = useTranslation("intake")

  const refr = ready ? computeRefractionKg(refraction, acceptedKg) : null
  const previewGrade =
    ready && refr && !refr.rejected
      ? classifyGradeWithDriver(classification)
      : null
  const refused = !!(refr && refr.rejected)

  const gradeToShow: LoadGrade | null = stamped
    ? (stampedGrade ?? null)
    : refused
      ? "REFUSED"
      : (previewGrade?.grade ?? null)

  const totalToShow = refr && !refr.rejected ? refr.totalKg : null

  const isRefusedPanel = refused || gradeToShow === "REFUSED"

  const sectionLabel =
    "text-[10px] font-semibold tracking-[0.08em] text-muted-foreground uppercase"

  return (
    <div
      className={cn(
        "flex flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-xs",
        isRefusedPanel &&
          "border-red-200 dark:border-red-500/30",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between border-b px-4 py-2.5",
          isRefusedPanel
            ? "border-red-200 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/5"
            : "bg-muted/40",
        )}
      >
        <div
          className={cn(
            "inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.08em] uppercase",
            isRefusedPanel
              ? "text-red-700 dark:text-red-300"
              : "text-foreground/80",
          )}
        >
          <FlaskConical className="size-3.5" />
          {t("live.label")}
        </div>
        <div className="flex items-center gap-2">
          {ready || stamped ? (
            <QualityMathDialog
              refraction={refraction}
              classification={classification}
              acceptedKg={acceptedKg}
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[10px] font-semibold uppercase tracking-wide"
                >
                  {t("math.toggle")}
                </Button>
              }
            />
          ) : null}
          {stamped ? (
            <Badge variant="secondary" className="text-[10px] uppercase">
              {t("live.stamped")}
            </Badge>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col divide-y">
        <div className="space-y-2 px-4 py-3">
          <div className={sectionLabel}>{t("live.conformity")}</div>
          {!ready && !stamped ? (
            <div className="text-muted-foreground/70 text-xs">
              {t("live.gradeWaiting")}
            </div>
          ) : refused && refr && refr.rejected ? (
            <div className="border-l-2 border-red-500 pl-3">
              <div className="text-red-700 dark:text-red-300 inline-flex items-center gap-1.5 text-xs font-semibold">
                <XCircle className="size-3.5" />
                {t("live.refusedHeading", {
                  metric: t(`lab.fields.${refr.reasonKey}`),
                })}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-3 text-[11px] tabular-nums">
                <div>
                  <div className="text-muted-foreground tracking-wide uppercase">
                    {t("live.refusedDetail.value")}
                  </div>
                  <div className="text-foreground font-semibold">
                    {formatLocaleNumber(refr.value)}%
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground tracking-wide uppercase">
                    {t("live.refusedDetail.limit")}
                  </div>
                  <div className="text-foreground font-semibold">
                    {formatLocaleNumber(refr.threshold)}%
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground tracking-wide uppercase">
                    {t("live.refusedDetail.excess")}
                  </div>
                  <div className="text-red-700 dark:text-red-300 font-semibold">
                    +
                    {formatLocaleNumber(Math.max(0, refr.value - refr.threshold))}
                    %
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-l-2 border-emerald-500 pl-3">
              <div className="text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1.5 text-xs font-medium">
                <CheckCircle2 className="size-3.5" />
                {t("live.passed")}
              </div>
            </div>
          )}
        </div>

        {totalToShow != null && !hideRefractionTotal ? (
          <div className="flex items-baseline justify-between gap-3 px-4 py-3">
            <div className={sectionLabel}>{t("live.totalRefraction")}</div>
            {totalToShow === 0 ? (
              <div className="text-foreground/70 text-sm tabular-nums">
                <span className="text-foreground font-semibold">0</span>
                <span className="text-muted-foreground ml-1 text-[11px]">
                  {t("live.kg")}
                </span>
              </div>
            ) : (
              <div className="flex items-baseline gap-1 tabular-nums">
                <span className="text-foreground text-lg font-semibold">
                  {formatLocaleNumber(totalToShow, 2)}
                </span>
                <span className="text-muted-foreground text-[11px]">
                  {t("live.kg")}
                </span>
              </div>
            )}
          </div>
        ) : null}

        <div className="space-y-2 px-4 py-3">
          <div className={sectionLabel}>{t("live.grade")}</div>
          {gradeToShow ? (
            <div className="space-y-1.5">
              <StatusBadge tone={gradeTone(gradeToShow)}>
                {t(`grade.${gradeToShow}`)}
              </StatusBadge>
              {!stamped && previewGrade && previewGrade.driver ? (
                <div className="text-muted-foreground text-[11px]">
                  {t("live.gradeDrivenBy", {
                    metric: t(`lab.fields.${previewGrade.driver.metric}`),
                    value: formatLocaleNumber(previewGrade.driver.value),
                    limit: formatLocaleNumber(previewGrade.driver.limit),
                  })}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-muted-foreground/70 text-xs">
              {t("live.gradeWaiting")}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
